import { useRef, useCallback } from 'react';
import { message } from 'antd';
import type { Graph } from '@antv/x6';
import { applyParallelEdgeOffset } from '../utils/edgeOffset';
import { mergeSameTypeEdges } from '../utils/mergeSameTypeEdges';
import { type ClipboardPayload, type ClipboardNode } from '../utils/nodeHelpers';

export interface UseClipboardOptions {
  graph: Graph | null;
  getSelectedNodes: () => any[];
  getNodesByIds: (ids: string[]) => any[];
  cleanGraphSelection: () => void;
  selectGraphNode: (node: any, mode: 'single' | 'group') => void;
  updateSelectionBoxFromNodes: (nodes: any[]) => void;
  setStatusText: (text: string) => void;
  onNodeSelect?: (node: any) => void;
}

export function useClipboard({
  graph,
  getSelectedNodes,
  getNodesByIds,
  cleanGraphSelection,
  selectGraphNode,
  updateSelectionBoxFromNodes,
  setStatusText,
  onNodeSelect,
}: UseClipboardOptions) {
  const clipboardRef = useRef<ClipboardPayload | null>(null);
  const pasteCountRef = useRef(0);
  const clipboardUsedRef = useRef(false);
  const lastPastedNodeIdsRef = useRef<string[]>([]);
  const lastCanvasClickRef = useRef<{ x: number; y: number } | null>(null);

  const copySelectedNodes = useCallback(() => {
    if (!graph) return;
    const selectedNodes = getSelectedNodes();
    if (!selectedNodes.length) return;

    const selectedIds = new Set(selectedNodes.map((n: any) => n.id));
    const topLevelNodes = selectedNodes.filter((node: any) => {
      const parent = node.getParent?.();
      return !parent || !selectedIds.has(parent.id);
    });

    const nodesToCopy = topLevelNodes.map((node: any) => {
      const data = { ...(node.getData() || {}) };
      delete data.selected;
      delete data.dragTarget;
      const children = (node.getChildren?.() || []).map((child: any) => {
        const childData = { ...(child.getData() || {}) };
        delete childData.selected;
        delete childData.dragTarget;
        return {
          id: child.id,
          shape: (child as any).shape,
          data: childData,
          position: child.getPosition(),
          size: child.getSize(),
        };
      });
      return {
        id: node.id,
        shape: (node as any).shape,
        data,
        position: node.getPosition(),
        size: node.getSize(),
        children,
      };
    });

    const nodeIdsToCopy = new Set<string>();
    nodesToCopy.forEach(node => {
      nodeIdsToCopy.add(node.id);
      node.children.forEach((child: ClipboardNode['children'][number]) => nodeIdsToCopy.add(child.id));
    });

    const edgesToCopy = graph
      .getEdges()
      .filter(edge => {
        const src = edge.getSourceCellId?.();
        const tgt = edge.getTargetCellId?.();
        return src && tgt && nodeIdsToCopy.has(src) && nodeIdsToCopy.has(tgt);
      })
      .map(edge => {
        const source = edge.getSource?.() as any;
        const target = edge.getTarget?.() as any;
        const rawData = { ...(edge.getData?.() || {}) };
        delete rawData._mergedEdge;
        return {
          id: edge.id,
          shape: (edge as any).shape,
          data: rawData,
          sourceId: edge.getSourceCellId?.() || '',
          targetId: edge.getTargetCellId?.() || '',
          sourcePort: source?.port,
          targetPort: target?.port,
          vertices: edge.getVertices?.() || undefined,
          router: edge.getRouter?.(),
          connector: edge.getConnector?.(),
          attrs: edge.getAttrs?.(),
        };
      });

    clipboardRef.current = { nodes: nodesToCopy, edges: edgesToCopy };
    pasteCountRef.current = 0;
    clipboardUsedRef.current = false;
    lastPastedNodeIdsRef.current = [];
    setStatusText(`已复制 ${nodesToCopy.length} 个节点`);
  }, [graph, getSelectedNodes, setStatusText]);

  const pasteClipboardNodes = useCallback(() => {
    if (!graph) return;
    const clipboard = clipboardRef.current;
    if (!clipboard || !clipboard.nodes.length) return;

    if (clipboardUsedRef.current) {
      const nodesToHighlight = getNodesByIds(lastPastedNodeIdsRef.current);
      if (nodesToHighlight.length) {
        cleanGraphSelection();
        nodesToHighlight.forEach(node => selectGraphNode(node, 'group'));
        updateSelectionBoxFromNodes(nodesToHighlight);
      }
      message.info('本次复制只允许粘贴一次，已高亮上一次粘贴的位置');
      setStatusText('已粘贴过一次，请重新复制后再粘贴');
      return;
    }

    pasteCountRef.current += 1;
    const newSelected: any[] = [];
    const idMap = new Map<string, string>();

    const allNodes = clipboard.nodes.flatMap(node => [node, ...node.children]);
    const minX = Math.min(...allNodes.map(n => n.position.x));
    const minY = Math.min(...allNodes.map(n => n.position.y));
    const anchor = lastCanvasClickRef.current;
    const deltaX = anchor ? anchor.x - minX : 30 * pasteCountRef.current;
    const deltaY = anchor ? anchor.y - minY : 30 * pasteCountRef.current;

    clipboard.nodes.forEach((item) => {
      const newNode = graph.addNode({
        shape: item.shape,
        x: item.position.x + deltaX,
        y: item.position.y + deltaY,
        width: item.size.width,
        height: item.size.height,
        data: { ...item.data, label: item.data.label },
      });
      idMap.set(item.id, newNode.id);
      newSelected.push(newNode);

      item.children.forEach((child) => {
        const relX = child.position.x - item.position.x;
        const relY = child.position.y - item.position.y;
        const newChild = graph.addNode({
          shape: child.shape,
          x: item.position.x + deltaX + relX,
          y: item.position.y + deltaY + relY,
          width: child.size.width,
          height: child.size.height,
          data: { ...child.data, label: child.data.label, parentId: newNode.id },
        });
        newNode.embed(newChild);
        idMap.set(child.id, newChild.id);
        newSelected.push(newChild);
      });
    });

    clipboard.edges.forEach((edge, idx: number) => {
      const sourceId = idMap.get(edge.sourceId);
      const targetId = idMap.get(edge.targetId);
      if (!sourceId || !targetId) return;

      const vertices = edge.vertices?.map(v => ({ x: v.x + deltaX, y: v.y + deltaY }));
      const source: { cell: string; port?: string } = { cell: sourceId };
      if (edge.sourcePort) source.port = edge.sourcePort;
      const target: { cell: string; port?: string } = { cell: targetId };
      if (edge.targetPort) target.port = edge.targetPort;

      graph.addEdge({
        id: `${edge.id}-copy-${Date.now()}-${idx}`,
        shape: edge.shape,
        source,
        target,
        data: { ...edge.data },
        attrs: edge.attrs,
        router: edge.router,
        connector: edge.connector,
        vertices,
      });
    });

    cleanGraphSelection();
    newSelected.forEach(node => selectGraphNode(node, 'group'));
    updateSelectionBoxFromNodes(newSelected);
    onNodeSelect?.(null);
    setStatusText(`已粘贴 ${newSelected.length} 个节点`);

    applyParallelEdgeOffset(graph);
    mergeSameTypeEdges(graph);
    clipboardUsedRef.current = true;
    lastPastedNodeIdsRef.current = newSelected.map(node => node.id);
  }, [graph, getNodesByIds, cleanGraphSelection, selectGraphNode, updateSelectionBoxFromNodes, onNodeSelect, setStatusText]);

  return {
    clipboardRef,
    lastCanvasClickRef,
    copySelectedNodes,
    pasteClipboardNodes,
  };
}
